import { useCallback, useEffect, useRef } from 'react';
import * as PIXI from 'pixi.js';
import type { Viewport } from 'pixi-viewport';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import useFurniturePreviewStore from '../../../stores/useFurniturePreviewStore';
import {
  estimateFootprintTilesFromSpriteWidth,
  estimateRugFootprintTiles,
  isWallMountedItem,
  estimateWindowFootprintTiles,
  getTileDims,
} from '../../../domain/tileMath';
import { beginFurnitureDragSession, type RoomDragState } from './dragSession';
import type { RenderFurniture } from '../../../domain/types';
import type { RoomSceneState } from '../state/roomSceneState';
import { asFurnitureSprite } from '../render/pixiExtensions';
import { mapSceneSingleton } from '../../map/mapScene';

// ── Furniture interaction cluster: drag / drop / tap radial menu ──
// Extracted verbatim from RoomCanvas (decomposition step: setupFurnitureInteraction).
// The body is byte-identical to the original except that:
//  * RoomCanvas-owned refs, state flags and radial-menu/outline callbacks are
//    received as parameters;
//  * the module-level `roomContainer` / `roomViewport` scene state is read through
//    `getRoomContainer()` / `getRoomViewport()` getters, shadowed as locals at the
//    top of each event handler (same read-at-invocation-time semantics).
// Holding an item only drags it; there is no furniture long-press.
// Constants used ONLY by this cluster moved here with it.

// DRAG_THRESHOLD / SURFACE_ZINDEX_BOOST / CONFLICT_FLOOR_ZINDEX live in ./dragMove.ts;
// and EMPTY_CARRIED_IDS with the drop recompute in ./dragUp.ts. One definition each.

// `RoomDragState` is defined in ./dragSession.ts (the drag session is its only writer)
// and re-exported here so existing consumers keep importing it from this module.
export type { RoomDragState };

export interface UseFurnitureInteractionParams {
  mapObjectId: number;
  access: FurnitureInteractionAccess;
  scene: FurnitureInteractionScene;
  sceneRef: React.MutableRefObject<RoomSceneState>;
  radialMenu: FurnitureInteractionRadialMenu;
  visualFeedback: FurnitureInteractionVisualFeedback;
}

export interface FurnitureInteractionAccess {
  canManageFurniture: boolean;
  houseFurnitureLoaded: boolean;
  houseFurnitureFetching: boolean;
  tileGridDebugEnabled: boolean;
}

export interface FurnitureInteractionScene {
  getRoomContainer: () => PIXI.Container | null;
  getRoomViewport: () => Viewport | null;
}

/** sceneRef.current groups replace the former bag of useRefs. */
export type FurnitureInteractionRefs = React.MutableRefObject<RoomSceneState>;

export interface FurnitureInteractionRadialMenu {
  openFurnitureRadialMenu: (marker: PIXI.Container, furniture: RenderFurniture, pointerX: number, pointerY: number) => void;
}

export interface FurnitureInteractionVisualFeedback {
  activateMarkerMenuVisualState: (marker: PIXI.Container) => void;
  removeGreenOutline: () => void;
  showGreenOutline: (marker: PIXI.Container) => void;
  logFurnitureDiagnostics: () => void;
}

export interface UseFurnitureInteractionResult {
  setupFurnitureInteraction: (sprite: PIXI.Sprite, furniture: RenderFurniture, markerContainer: PIXI.Container) => void;
}

export function useFurnitureInteraction({
  mapObjectId,
  access: {
    canManageFurniture,
    houseFurnitureLoaded,
    houseFurnitureFetching,
    tileGridDebugEnabled,
  },
  scene: {
    getRoomContainer,
    getRoomViewport,
  },
  sceneRef,
  radialMenu: {
    openFurnitureRadialMenu,
  },
  visualFeedback: {
    activateMarkerMenuVisualState,
    removeGreenOutline,
    showGreenOutline,
    logFurnitureDiagnostics,
  },
}: UseFurnitureInteractionParams): UseFurnitureInteractionResult {
  const queryClient = useQueryClient();
  // The drag session reads these at RELEASE time (to record the undo step), long
  // after setupFurnitureInteraction captured its closure — read them live.
  const gatesRef = useRef({ canManageFurniture, houseFurnitureLoaded, houseFurnitureFetching });
  useEffect(() => {
    gatesRef.current = { canManageFurniture, houseFurnitureLoaded, houseFurnitureFetching };
  });
  const { t } = useTranslation();
  const {
    updatePreviewPosition,
    updatePreviewPlacementMode,
    togglePreviewMirror,
    removePreviewItem,
  } = useFurniturePreviewStore();

  const setupFurnitureInteraction = useCallback((sprite: PIXI.Sprite, furniture: RenderFurniture, markerContainer: PIXI.Container) => {
    const app = mapSceneSingleton.app;
    if (!furniture.furniture_catalog) {
      // console.warn(`[RoomCanvas] Cannot setup interaction for furniture ${furniture.id} - missing furniture_catalog`);
      return;
    }

    const fs = asFurnitureSprite(sprite)!;
    const spriteWidth = sprite ? Math.abs(sprite.width) : 200;
    const { w: tileDimW, h: tileDimH, isRug: interactionIsRug } = getTileDims(furniture.furniture_catalog);

    // Always read the live catalog from sprite.furnitureData so that admin tile
    // edits (footprint_tiles / surface_tiles saved after interaction setup) are
    // picked up by the drag handlers without requiring a full re-setup.
    // sprite.furnitureData is kept in sync on every renderObjects call (line ~1987).
    const getLiveCatalog = (): typeof furniture.furniture_catalog =>
      fs.furnitureData?.furniture_catalog
      ?? furniture.furniture_catalog;

    let rawEffectiveWidthTiles: number;
    let rawEffectiveHeightTiles: number;

    const isWindow = isWallMountedItem(furniture.furniture_catalog);

    if (interactionIsRug && (tileDimW === null || tileDimW === undefined) && (tileDimH === null || tileDimH === undefined)) {
      const rugEst = estimateRugFootprintTiles(
        spriteWidth,
        sprite ? Math.abs(sprite.height) : 200,
        fs.__visualBaseOffsetX || 0,
      );
      rawEffectiveWidthTiles = rugEst.width;
      rawEffectiveHeightTiles = rugEst.height;
    } else if (isWindow && (tileDimW === null || tileDimW === undefined) && (tileDimH === null || tileDimH === undefined)) {
      // Window-specific: width from widest opaque row, thickness always 1
      // Must match estimateWindowFootprintTiles used in getFurnitureDepthComponents
      const maxOpaqueW = fs.__maxOpaqueWidthPx || 0;
      const windowEst = estimateWindowFootprintTiles(maxOpaqueW);
      rawEffectiveWidthTiles = windowEst.width;
      rawEffectiveHeightTiles = windowEst.height;
    } else {
      const estimated = estimateFootprintTilesFromSpriteWidth(spriteWidth);
      rawEffectiveWidthTiles = (tileDimW === null || tileDimW === undefined)
        ? estimated.width
        : Math.max(1, tileDimW);
      rawEffectiveHeightTiles = (tileDimH === null || tileDimH === undefined)
        ? estimated.height
        : Math.max(1, tileDimH);
    }

    // The whole drag session — per-drag tile maps, the window snap closure, the
    // and the nested pointermove/pointerup
    // handlers — was extracted verbatim to ./dragSession.ts. Everything the body
    // used is wired explicitly below; getters keep read-at-invocation-time
    // semantics for the mutable scene state.
    const onPointerDown = (event: PIXI.FederatedPointerEvent) => {
      beginFurnitureDragSession(event, {
        scene: {
          getRoomContainer,
          getRoomViewport,
          app,
        },
        refs: {
          furniture: sceneRef.current.furniture,
          drag: sceneRef.current.drag,
          overlays: sceneRef.current.overlays,
          flags: sceneRef.current.flags,
          admin: sceneRef.current.admin,
        },
        item: {
          sprite,
          furniture,
          markerContainer,
        },
        geometry: {
          tileDimW,
          tileDimH,
          interactionIsRug,
          isWindow,
          rawEffectiveWidthTiles,
          rawEffectiveHeightTiles,
          getLiveCatalog,
        },
        radialMenu: {
          openFurnitureRadialMenu,
        },
        selectionOutline: {
          showGreenOutline,
          removeGreenOutline,
          logFurnitureDiagnostics,
        },
        gates: {
          get canManageFurniture() { return gatesRef.current.canManageFurniture; },
          get houseFurnitureLoaded() { return gatesRef.current.houseFurnitureLoaded; },
          get houseFurnitureFetching() { return gatesRef.current.houseFurnitureFetching; },
        },
        services: {
          mapObjectId,
          queryClient,
          t,
        },
        previewActions: {
          updatePreviewPosition,
          updatePreviewPlacementMode,
          togglePreviewMirror,
        },
      });
    };

    sprite.on('pointerdown', onPointerDown);
    // Identity setupFurnitureInteraction має лишатись стабільною відносно furniture-render:
    // зміна = re-attach pointerdown на кожен sprite. canManageFurniture / houseFurniture* /
    // openFurnitureRadialMenu / show|removeGreenOutline /
    // updatePreviewPlacementMode / getRoom* — часто нові refs щорендеру (store/TDZ wrappers).
    // sceneRef стабільний — додаємо. Решта читається в pointerdown (event-time).
    // eslint-disable-next-line react-hooks/exhaustive-deps -- event-time handlers; avoid re-bind thrash
  }, [mapObjectId, queryClient, t, activateMarkerMenuVisualState, updatePreviewPosition, removePreviewItem, togglePreviewMirror, tileGridDebugEnabled, logFurnitureDiagnostics, sceneRef]);
  return { setupFurnitureInteraction };
}
